import { Jimp } from "jimp";
import UPNG from "upng-js";

export async function createBase64FromRawPixels(
	rawPixels: Uint8Array,
	width: number,
	height: number,
	paddingOptions?: {
		top: number;
		bottom: number;
		left: number;
		right: number;
		color: number[];
	},
	targetSize?: {
		width: number;
		height: number;
	},
) {
	const pixelCount = width * height;
	let rgbaPixels: Uint8Array;

	if (rawPixels.length === pixelCount * 4) {
		rgbaPixels = rawPixels;
	} else if (rawPixels.length === pixelCount * 3) {
		rgbaPixels = new Uint8Array(pixelCount * 4);
		for (let i = 0; i < pixelCount; i++) {
			rgbaPixels[i * 4] = rawPixels[i * 3];
			rgbaPixels[i * 4 + 1] = rawPixels[i * 3 + 1];
			rgbaPixels[i * 4 + 2] = rawPixels[i * 3 + 2];
			rgbaPixels[i * 4 + 3] = 255;
		}
	} else {
		throw new Error(
			"Raw data length does not match width and height (expected RGB or RGBA)",
		);
	}
	const {
		pixelData,
		width: newWidth,
		height: newHeight,
	} = paddingOptions
		? addPaddingToPixelData(rgbaPixels, width, height, paddingOptions, 4)
		: { pixelData: rgbaPixels, width, height };
	let image = Jimp.fromBitmap({
		data: pixelData,
		width: newWidth,
		height: newHeight,
	});
	if (
		targetSize &&
		(targetSize.width !== newWidth || targetSize.height !== newHeight)
	) {
		image = image.resize({ w: targetSize.width, h: targetSize.height });
	}
	return (await image.getBase64("image/png")) as string;
}

export async function createBase64FromRawGrays(
	rawGrays: Uint8Array,
	width: number,
	height: number,
	paddingOptions?: {
		top: number;
		bottom: number;
		left: number;
		right: number;
		color: number[];
	},
	targetSize?: {
		width: number;
		height: number;
	},
) {
	if (rawGrays.length !== width * height) {
		throw new Error("Raw data length does not match width and height");
	}
	const {
		pixelData,
		width: newWidth,
		height: newHeight,
	} = paddingOptions
		? addPaddingToPixelData(rawGrays, width, height, paddingOptions, 1)
		: { pixelData: rawGrays, width, height };
	const rgba = new Uint8Array(newWidth * newHeight * 4);
	for (let i = 0; i < newWidth * newHeight; i++) {
		const gray = pixelData[i];
		const base = i * 4;
		rgba[base] = gray;
		rgba[base + 1] = gray;
		rgba[base + 2] = gray;
		rgba[base + 3] = 255;
	}
	let image = Jimp.fromBitmap({
		data: rgba,
		width: newWidth,
		height: newHeight,
	});
	if (
		targetSize &&
		(targetSize.width !== newWidth || targetSize.height !== newHeight)
	) {
		image = image.resize({ w: targetSize.width, h: targetSize.height });
	}
	return (await image.getBase64("image/png")) as string;
}

export function addPaddingToPixelData(
	pixelData: Uint8Array,
	width: number,
	height: number,
	paddingOptions: {
		top: number;
		bottom: number;
		left: number;
		right: number;
		color: number[];
	},
	channels: number,
) {
	const newWidth = width + paddingOptions.left + paddingOptions.right;
	const newHeight = height + paddingOptions.top + paddingOptions.bottom;
	const newPixelData = new Uint8Array(newWidth * newHeight * channels);

	for (let i = 0; i < newPixelData.length; i += channels) {
		newPixelData.set(paddingOptions.color, i);
	}

	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			const srcIdx = (y * width + x) * channels;
			const dstX = x + paddingOptions.left;
			const dstY = y + paddingOptions.top;
			const dstIdx = (dstY * newWidth + dstX) * channels;
			newPixelData.set(pixelData.slice(srcIdx, srcIdx + channels), dstIdx);
		}
	}

	return {
		pixelData: newPixelData,
		width: newWidth,
		height: newHeight,
	};
}

export function base64ToUint8Array(base64: string) {
	if (base64.startsWith("data:image/")) {
		base64 = base64.split(",")[1];
	}
	const buffer = Base64Binary.decodeArrayBuffer(base64);

	const img = UPNG.decode(buffer);
	const rgba = UPNG.toRGBA8(img)[0];
	return {
		width: img.width,
		height: img.height,
		rawPixels: rgba,
	};
}

export function getPngInfoFromUint8Array(uint8Array: Uint8Array) {
	const offset = 16;
	const view = new DataView(
		uint8Array.buffer,
		uint8Array.byteOffset,
		uint8Array.byteLength,
	);

	const width = view.getUint32(offset, false); // big-endian
	const height = view.getUint32(offset + 4, false);
	const colorType = view.getUint8(offset + 9); // color type은 9번째 바이트

	const hasAlpha = colorType === 4 || colorType === 6;

	return {
		width,
		height,
		colorType,
		hasAlpha,
	};
}

const Base64Binary = {
	_keyStr: "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=",

	// Base64 문자열을 디코딩하여 ArrayBuffer로 반환
	decodeArrayBuffer(input: string): ArrayBuffer {
		const bytes = (input.length / 4) * 3;
		const ab = new ArrayBuffer(bytes);
		this.decode(input, ab);
		return ab;
	},

	// 패딩 문자("=") 제거
	removePaddingChars(input: string): string {
		const lkey = this._keyStr.indexOf(input.charAt(input.length - 1));
		if (lkey === 64) {
			return input.substring(0, input.length - 1);
		}
		return input;
	},

	// Base64 문자열을 디코딩하여 Uint8Array로 반환
	decode(input: string, arrayBuffer?: ArrayBuffer): Uint8Array {
		input = this.removePaddingChars(this.removePaddingChars(input));
		const bytes = Math.floor((input.length / 4) * 3);

		let uarray: Uint8Array;
		let chr1: number, chr2: number, chr3: number;
		let enc1: number, enc2: number, enc3: number, enc4: number;
		let i = 0,
			j = 0;

		uarray = arrayBuffer ? new Uint8Array(arrayBuffer) : new Uint8Array(bytes);

		input = input.replace(/[^A-Za-z0-9+/=]/g, "");

		for (i = 0; i < bytes; i += 3) {
			enc1 = this._keyStr.indexOf(input.charAt(j++));
			enc2 = this._keyStr.indexOf(input.charAt(j++));
			enc3 = this._keyStr.indexOf(input.charAt(j++));
			enc4 = this._keyStr.indexOf(input.charAt(j++));

			chr1 = (enc1 << 2) | (enc2 >> 4);
			chr2 = ((enc2 & 15) << 4) | (enc3 >> 2);
			chr3 = ((enc3 & 3) << 6) | enc4;

			uarray[i] = chr1;
			if (enc3 !== 64) uarray[i + 1] = chr2;
			if (enc4 !== 64) uarray[i + 2] = chr3;
		}

		return uarray;
	},
};
